import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { WEB_SDK_TESTS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { IUseTableQueryParams } from 'src/common/types/UseTableQueryParamsType';

import { getWebSdkTests, getWebSdkTestsV2 } from '../services/WebSdkDashboardService';
import { TWebSdkTestsResult } from '../types/WebSdkDashboardServiceTypes';

interface IUseWebSdkTestsQueryParams extends IUseTableQueryParams {
	showRecentTest: boolean;
	showTestPath: boolean;
	isNewPlatformEnabled?: boolean;
}

export const useWebSdkTestsQuery = ({
	showRecentTest,
	filters,
	sort,
	page,
	pageSize,
	showTestPath,
	isNewPlatformEnabled = false
}: IUseWebSdkTestsQueryParams): [UseQueryResult<TWebSdkTestsResult>, () => void] => {
	const v2QueryResult = useQuery(
		[WEB_SDK_TESTS, showRecentTest, filters, sort, page, pageSize, showTestPath],
		async (): Promise<TWebSdkTestsResult> =>
			getWebSdkTestsV2({
				isRecentTests: showRecentTest,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				isShowTestPath: showTestPath
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL,
			enabled: isNewPlatformEnabled
		}
	);

	const originalQueryResult = useQuery(
		[WEB_SDK_TESTS, showRecentTest, filters, sort, page, pageSize, showTestPath],
		async (): Promise<TWebSdkTestsResult> =>
			getWebSdkTests({
				isRecentTests: showRecentTest,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				isShowTestPath: showTestPath
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL,
			enabled: !isNewPlatformEnabled
		}
	);

	const invalidate = useCallback(
		() => queryClient.invalidateQueries({ queryKey: [WEB_SDK_TESTS], refetchType: 'none' }),
		[]
	);

	return isNewPlatformEnabled ? [v2QueryResult, invalidate] : [originalQueryResult, invalidate];
};
